import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Navbar.scss";
import { Logo, ThemeToggle } from "../";
import { OPEN_PALETTE_EVENT } from "../CommandPalette/CommandPalette";
import { profile, sections } from "../../data/portfolio";
import { goToSection, scrollToId } from "../../utils/scroll";

const navSections = sections.filter((section) => section.id !== "home" && section.nav !== false);

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState("home");
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setScrolled(window.scrollY > 12);
      setProgress(max > 0 ? window.scrollY / max : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Highlight the section currently in view.
  useEffect(() => {
    if (!isHome) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [isHome]);

  // Scroll to the hash after arriving on the homepage from another route.
  useEffect(() => {
    if (!location.hash) return undefined;
    const timer = setTimeout(() => scrollToId(location.hash.substring(1)), 150);
    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const handleNav = (event, id) => {
    event.preventDefault();
    setMenuOpen(false);
    goToSection(id, location, navigate);
  };

  return (
    <header className={`site-nav ${scrolled ? "is-scrolled" : ""} ${menuOpen ? "menu-open" : ""}`}>
      <div className="site-nav__progress" style={{ transform: `scaleX(${progress})` }} />
      <nav className="site-nav__bar" aria-label="Primary">
        <Link to="/" className="site-nav__brand" onClick={(event) => handleNav(event, "home")}>
          <Logo size="small" />
          <span className="site-nav__name">Paradzai <span>Gwira</span></span>
        </Link>

        <ul className="site-nav__links">
          {navSections.map((section) => (
            <li key={section.id}>
              <a
                href={`/#${section.id}`}
                className={isHome && activeId === section.id ? "is-active" : ""}
                onClick={(event) => handleNav(event, section.id)}
              >
                {section.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="site-nav__actions">
          <button type="button" className="site-nav__cmdk" onClick={() => window.dispatchEvent(new Event(OPEN_PALETTE_EVENT))} aria-label="Open command palette">
            <i className="fas fa-magnifying-glass" />
            <span>Search</span>
            <kbd>⌘K</kbd>
          </button>
          <ThemeToggle />
          <Link data-magnetic className="site-nav__cta" to={profile.cvPage} onClick={() => setMenuOpen(false)}>
            <i className="fas fa-id-card" /> CV
          </Link>
          <button
            type="button"
            className="site-nav__burger"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span /><span />
          </button>
        </div>
      </nav>

      <div className="site-nav__mobile" aria-hidden={!menuOpen}>
        <ul>
          {navSections.map((section, index) => (
            <li key={section.id} style={{ transitionDelay: `${menuOpen ? 60 + index * 45 : 0}ms` }}>
              <a href={`/#${section.id}`} tabIndex={menuOpen ? 0 : -1} onClick={(event) => handleNav(event, section.id)}>
                <span>0{index + 1}</span>{section.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="site-nav__mobile-foot">
          <a href={`mailto:${profile.email}`} tabIndex={menuOpen ? 0 : -1}>{profile.email}</a>
          <Link to={profile.cvPage} tabIndex={menuOpen ? 0 : -1} onClick={() => setMenuOpen(false)}>View CV →</Link>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
